import { useCallback, useEffect, useMemo } from 'react';
import find from 'lodash/find';
import isArray from 'lodash/isArray';

import { FetchFinInvoicesFilters, InvoiceID } from '../../invoicesTypes';
import {
  MultiSelectChangeCallbackType,
  MultiSelectDataType
} from '../../../../helpers/MultiSelect/types';

import {
  FETCH_CACHE_INVOICES_SELECT_QUERY,
  FetchCacheInvoicesSelectQueryResponse
} from '../../queries/fetchCacheInvoicesSelect.query';

import { usePreviousValue } from '../../../../common/hooks/usePreviousValue';
import { useCachePaginatedInvoices } from '../../hooks/useCachePaginatedInvoices';

import { MultiSelect, MultiSelectProps } from '../../../../helpers/MultiSelect';
import { InvoiceCache } from '../../InvoiceCache';

const initialLimit = 100;

interface TagsMultiSelectProps {
  value: InvoiceID[];
  autoFocus?: boolean;
  onChange: (value: InvoiceID[]) => void;
  initialFilters: FetchFinInvoicesFilters;
}

function InvoicesMultiSelect({
  value,
  autoFocus,
  onChange,
  initialFilters
}: TagsMultiSelectProps & MultiSelectProps) {
  const { invoices, invoicesError, invoicesLoading, filterInvoices } =
    useCachePaginatedInvoices<FetchCacheInvoicesSelectQueryResponse>({
      cacheKey: InvoiceCache.selectCacheKey(),
      initialLimit: initialLimit,
      initialFilters: initialFilters,
      query: FETCH_CACHE_INVOICES_SELECT_QUERY,
      options: {
        withoutPrefetch: true
      }
    });

  const prevFilters = usePreviousValue(initialFilters);

  useEffect(() => {
    if (prevFilters !== initialFilters) {
      filterInvoices(initialFilters);
    }
  }, [filterInvoices, initialFilters, prevFilters]);

  const invoicesData = useMemo<MultiSelectDataType[]>(() => {
    return invoices.map((item) => ({
      label: item.name || item.clientId,
      value: item.id
    }));
  }, [invoices]);

  const handleChange = useCallback<MultiSelectChangeCallbackType>(
    (value: MultiSelectDataType) => {
      onChange(isArray(value) ? value.map((item) => item.value) : []);
    },
    [onChange]
  );

  const multiSelectValue = useMemo<MultiSelectDataType[]>(() => {
    return isArray(value)
      ? value.map((item) => find(invoicesData, ['value', item]))
      : [];
  }, [value, invoicesData]);

  return (
    <MultiSelect
      error={invoicesError}
      inputWrapperClassName="block"
      onChange={handleChange}
      value={multiSelectValue}
      multi={true}
      isSearchable={true}
      optionsLoading={invoicesLoading}
      isLoading={invoicesLoading}
      data={invoicesData}
      classNamePrefix="av_multi_select"
      autoFocus={autoFocus}
    />
  );
}

export default InvoicesMultiSelect;
